/**
 * KommunikationZeroState — the ⭐ primary MC6 surface (AC1).
 *
 * Shown in place of the Eingang mail-split when the mailbox feature is
 * `available` (enabled) with ZERO connected mailboxes: a centered card with a
 * muted icon, a navy page-title heading, a one-sentence subtext, and a single
 * Primary CTA "Postfach verbinden" that opens the shared ProviderChoiceModal.
 * A secondary "Postfächer verwalten" link reaches /admin/postfaecher (ungated,
 * requiredKeys:[] — reachable by every broker, AC4/AC9).
 *
 * [Source: docs/design/2026-07-10_sebastian-package/design.md §8 Empty states + §3 page title + §5 one Primary]
 */

import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Mailbox } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { ProviderChoiceModal } from './ProviderChoiceModal';
import type { MailboxAvailability } from '../hooks/useMailboxConnections';

export function KommunikationZeroState({
  availability = 'available',
}: {
  availability?: MailboxAvailability;
}) {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-4 p-8 text-center">
      <span className="grid size-14 place-items-center rounded-2xl bg-muted text-muted-foreground/70">
        <Mailbox className="size-7" />
      </span>
      <div className="flex max-w-md flex-col gap-1.5">
        <h2 className="text-[22px] font-semibold italic tracking-tight text-foreground [font-family:var(--font-display,inherit)]">
          Verbinde zuerst dein E-Mail-Postfach
        </h2>
        <p className="text-sm text-muted-foreground">
          Die Kommunikation zeigt deinen echten Posteingang, sobald du dein persönliches
          Microsoft-365- oder Google-Workspace-Postfach verbunden hast. Danach kannst du hier
          direkt antworten und neue Nachrichten senden.
        </p>
      </div>
      <div className="flex flex-col items-center gap-2">
        <Button onClick={() => setModalOpen(true)}>Postfach verbinden</Button>
        <Link
          to="/admin/$section"
          params={{ section: 'postfaecher' }}
          className="text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
        >
          Postfächer verwalten
        </Link>
      </div>

      <ProviderChoiceModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        availability={availability}
      />
    </div>
  );
}
